import { HYDRATE } from 'next-redux-wrapper'

import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit'
import isEqual from 'lodash/isEqual'

import { SavedFilterI } from '@/features/incidents-table'
import {
  IncidentFilterCriteria,
  IncidentStatus,
  MutedStatus,
} from '@/graphql/generated/schemas'

import type { AppState } from '../store'
import { buildFilterInput } from './conversion'
import type { DeepLinkIncidentFilterCriteria } from './deepLink'
import { removeDefaultMutedStatusAll, removeNullValues } from './utils'

export type ExtraIncidentFilterCriteria = {
  query?: string
  ownerId?: string
}

export interface IncidentsStateIProps {
  activeFilter: SavedFilterI | null
  activeFilterCriteria: IncidentFilterCriteria
  extraFilterCriteria: ExtraIncidentFilterCriteria
  remoteActiveFilter: SavedFilterI | null
  remoteActiveFilterCriteria: IncidentFilterCriteria | null
  remoteActiveFilterLoaded: boolean
  pageSize: number
}

export const initialState: IncidentsStateIProps = {
  activeFilter: null,
  activeFilterCriteria: {
    statusIn: [IncidentStatus.Active, IncidentStatus.InProgress],
    mutedStatus: MutedStatus.NotMuted,
  },
  extraFilterCriteria: {
    query: null,
    ownerId: null,
  },
  remoteActiveFilter: null,
  remoteActiveFilterCriteria: null,
  remoteActiveFilterLoaded: false,
  pageSize: 25,
}

const applyDeepLinkCriteria = (
  state: IncidentsStateIProps,
  deepLinkCriteria: DeepLinkIncidentFilterCriteria
) => {
  state.activeFilter = null
  state.extraFilterCriteria = { ...initialState.extraFilterCriteria }
  state.activeFilterCriteria = { ...deepLinkCriteria }
}

const applyRemoteSnapshot = (state: IncidentsStateIProps) => {
  state.activeFilter = state.remoteActiveFilter
  state.activeFilterCriteria =
    state.remoteActiveFilterCriteria || initialState.activeFilterCriteria
}

export const incidentsSlice = createSlice({
  name: 'incidents',
  initialState,
  reducers: {
    setActiveFilter: (state, action: PayloadAction<SavedFilterI | null>) => {
      state.activeFilter = action.payload
    },
    setActiveFilterCriteria: (
      state,
      action: PayloadAction<IncidentFilterCriteria>
    ) => {
      state.activeFilterCriteria = action.payload
    },
    setRemoteActiveFilterSnapshot: (
      state,
      action: PayloadAction<{
        activeFilter: SavedFilterI | null
        activeFilterCriteria: IncidentFilterCriteria | null
      }>
    ) => {
      state.remoteActiveFilter = action.payload.activeFilter
      state.remoteActiveFilterCriteria = action.payload.activeFilterCriteria
      state.remoteActiveFilterLoaded = true
    },
    initializeIncidentsFilters: (
      state,
      action: PayloadAction<{
        deepLinkCriteria: DeepLinkIncidentFilterCriteria | null
      }>
    ) => {
      if (action.payload.deepLinkCriteria) {
        applyDeepLinkCriteria(state, action.payload.deepLinkCriteria)
        return
      }

      if (!state.remoteActiveFilterLoaded) return

      applyRemoteSnapshot(state)
    },
    syncActiveFilterFromRemote: (state) => {
      if (!state.remoteActiveFilterLoaded) return

      applyRemoteSnapshot(state)
    },
    setExtraFilterCriteria: (
      state,
      action: PayloadAction<ExtraIncidentFilterCriteria>
    ) => {
      state.extraFilterCriteria = action.payload
    },
    setIncidentsPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload
    },
    setIncidentQueryFilter: (state, action: PayloadAction<string | null>) => {
      state.extraFilterCriteria = {
        ...state.extraFilterCriteria,
        ...{ query: action.payload },
      }
    },
    setIncidentOwnerFilter: (state, action: PayloadAction<string | null>) => {
      state.extraFilterCriteria = {
        ...state.extraFilterCriteria,
        ...{ ownerId: action.payload },
      }
    },
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      return {
        ...state,
        ...action.payload.subject,
      }
    },
  },
})

export const {
  setActiveFilter,
  setIncidentQueryFilter,
  setIncidentOwnerFilter,
  setIncidentsPageSize,
  setActiveFilterCriteria,
  setExtraFilterCriteria,
  setRemoteActiveFilterSnapshot,
  initializeIncidentsFilters,
  syncActiveFilterFromRemote,
} = incidentsSlice.actions

export const selectActiveFilter = (state: AppState) =>
  state.incidents?.activeFilter

export const selectActiveFilterCriteria = (state: AppState) =>
  state.incidents?.activeFilterCriteria

export const selectExtraFilterCriteria = (state: AppState) =>
  state.incidents?.extraFilterCriteria

export const selectIncidentsPageSize = (state: AppState) =>
  state.incidents?.pageSize

export const selectRemoteActiveFilterLoaded = (state: AppState) =>
  state.incidents?.remoteActiveFilterLoaded

export const selectRemoteActiveFilter = (state: AppState) =>
  state.incidents?.remoteActiveFilter

export const selectRemoteActiveFilterCriteria = (state: AppState) =>
  state.incidents?.remoteActiveFilterCriteria

export const selectIncidentOwnerIdFilter = (state: AppState) =>
  state?.incidents?.extraFilterCriteria?.ownerId

export const selectHasNonDefaultFilters = createSelector(
  selectActiveFilter,
  selectActiveFilterCriteria,
  selectExtraFilterCriteria,
  (activeFilter, activeFilterCriteria, extraFilterCriteria) => {
    return (
      Boolean(activeFilter) ||
      !isEqual(activeFilterCriteria, initialState.activeFilterCriteria) ||
      !isEqual(extraFilterCriteria, initialState.extraFilterCriteria)
    )
  }
)

export const selectIncidentFilterCount = createSelector(
  selectActiveFilterCriteria,
  selectExtraFilterCriteria,
  (activeFilterCriteria, extraFilterCriteria) => {
    const activeFilterCriteriaFiltered =
      // Remove MutedStatus.All filter from the count, this is a default filter which the user does not apply.
      // This count represents the filter count applied by the user
      removeDefaultMutedStatusAll(activeFilterCriteria)
    const savedFilterCount = Object.keys(
      removeNullValues(activeFilterCriteriaFiltered || {})
    ).length
    const extraFilterCount = extraFilterCriteria?.ownerId ? 1 : 0
    return savedFilterCount + extraFilterCount
  }
)

export const selectIncidentsFilterInput = createSelector(
  selectActiveFilterCriteria,
  selectExtraFilterCriteria,
  (activeFilterCriteria, extraFilterCriteria) =>
    buildFilterInput(activeFilterCriteria, extraFilterCriteria)
)

export const selectIncidentsActiveFilterInput = createSelector(
  selectActiveFilterCriteria,
  (activeFilterCriteria) => buildFilterInput(activeFilterCriteria)
)

// Default export for reducers is a pattern recommended by Redux
// See here: <https://redux.js.org/tutorials/fundamentals/part-3-state-actions-reducers#combinereducers>
// eslint-disable-next-line import/no-default-export
export default incidentsSlice.reducer
